import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Progress,
  Select,
  Spinner,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import {
  Archive,
  CaretLeft,
  CaretRight,
  CheckCircle,
  CloudArrowUp,
  EnvelopeSimple,
  Eye,
  Question,
  ShieldCheck,
  Warning,
} from 'phosphor-react';

import { GlassCard } from '../../../../../components/glass';
import { EmpresaPicker } from './EmpresaPicker';
import { ExtratoPdfModal } from '../extratos/components/ExtratoPdfModal';
import {
  IContaCilImportItem,
  IContaCilStatus,
  IEmpresaOption,
  IMPORT_WARN_MIN,
  minutosDesde,
  useContaCilAtribuirEmpresa,
  useContaCilImportar,
  useContaCilImports,
  useContaCilImportStatus,
} from '../../../../../hooks/useContaCilImportar';
import { extractApiErrorMessage } from '../../../../../utils/ErrorUtils';

const ACCEPT = {
  'application/pdf': ['.pdf'],
  'application/x-ofx': ['.ofx'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
  'text/csv': ['.csv'],
  'application/zip': ['.zip'],
  'application/octet-stream': ['.ofx'],
};

// Importacao manual pela interface desligada: os extratos novos chegam pelo
// fluxo automatico por e-mail. So a exposicao na tela sai — hook, dropzone e
// endpoint de upload seguem intactos. Para reativar, volte para true.
const IMPORTACAO_MANUAL_HABILITADA = false;

const IMPORTS_KEY = ['contacil', 'imports'];
const PAGE_SIZE = 5;
const FETCH_LIMIT = 200;

// Ordem por status: acionaveis (a identificar) no topo, concluidos por ultimo.
const STATUS_ORDER: Record<IContaCilStatus, number> = {
  a_identificar: 0,
  aguardando_titular: 0,
  aguardando_validacao: 1,
  quarentena: 2,
  processando: 3,
  erro: 4,
  concluido: 5,
};

type ImportFiltro = 'recentes' | 'pendentes';

const dropzoneLabel = (pending: boolean, drag: boolean): string => {
  if (pending) return 'Enviando...';
  if (drag) return 'Solte o arquivo aqui';
  return 'Arraste o extrato ou clique para selecionar';
};

const STATUS_UI: Record<IContaCilStatus, { cor: string; badge: string }> = {
  processando: { cor: '#1B88F5', badge: 'blue' },
  concluido: { cor: '#10B981', badge: 'green' },
  erro: { cor: '#EF4444', badge: 'red' },
  a_identificar: { cor: '#F59E0B', badge: 'orange' },
  aguardando_titular: { cor: '#F59E0B', badge: 'orange' },
  aguardando_validacao: { cor: '#8B5CF6', badge: 'purple' },
  quarentena: { cor: '#F97316', badge: 'orange' },
};

const renderStatusIcon = (status: IContaCilStatus) => {
  if (status === 'erro') return <Warning size={20} weight="duotone" />;
  if (status === 'concluido') return <CheckCircle size={20} weight="duotone" />;
  if (status === 'a_identificar') return <Question size={20} weight="duotone" />;
  if (status === 'aguardando_titular') return <Question size={20} weight="duotone" />;
  if (status === 'aguardando_validacao') return <ShieldCheck size={20} weight="duotone" />;
  if (status === 'quarentena') return <Archive size={20} weight="duotone" />;
  return <Spinner size="sm" thickness="2px" color="#1B88F5" />;
};

const badgeText = (status: IContaCilStatus, percent: number): string => {
  if (status === 'erro') return 'erro';
  if (status === 'concluido') return 'concluído';
  if (status === 'a_identificar') return 'a identificar';
  if (status === 'aguardando_titular') return 'aguardando titular';
  if (status === 'aguardando_validacao') return 'aguardando validação';
  if (status === 'quarentena') return 'quarentena';
  return `${percent}%`;
};

const fmtBRL = (v: number) =>
  v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface ImportStatusRowProps {
  item: IContaCilImportItem;
}

function ImportStatusRow({ item }: ImportStatusRowProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  // So consulta o /status (progresso transitorio) enquanto processando;
  // estados finais vem da propria lista (persistente).
  const isProcessando = item.status === 'processando';
  const { data } = useContaCilImportStatus(item.import_id, isProcessando);
  const [empresa, setEmpresa] = useState<IEmpresaOption | null>(null);
  const { mutateAsync: atribuir, isPending: atribuindo } = useContaCilAtribuirEmpresa();

  // So usa o /status transitorio enquanto processando; ao virar estado final
  // a lista (persistente) manda. Sem isso, o `data` velho do /status
  // (ja desabilitado) sobrepunha o item.status final e congelava o card em
  // "65% extraindo" (ou nao atualizava apos atribuir empresa).
  const live = isProcessando ? data : undefined;
  const status: IContaCilStatus = live?.status ?? item.status;
  const percent = status === 'concluido' ? 100 : (live?.percent ?? 0);
  const banco = live?.banco ?? item.banco;
  const qtdNovas = live?.qtd_novas ?? item.qtd_novas;
  const titular = live?.titular_detectado ?? item.titular_detectado;
  const cnpj = live?.cnpj_detectado ?? item.cnpj_detectado;
  const ui = STATUS_UI[status];
  const [pdfOpen, setPdfOpen] = useState(false);
  const navigate = useNavigate();
  // Enquanto processa não existe extrato montado pra abrir. Nos demais estados
  // existe registro — inclusive nos que exigem ação (a identificar, retido,
  // erro), que são justamente os que o operador precisa alcançar.
  const podeAbrirExtrato = status !== 'processando' && item.import_id != null;
  const abrirNoExtratos = () =>
    navigate(
      `/modulesHome/accountingConciliation/conciliacao-contabil/extratos?extrato=${item.import_id}`,
    );
  // O arquivo fica no S3 desde o upload (import_id == id do extrato); so' nao
  // oferece o preview enquanto ainda esta processando.
  const podeVisualizar = status !== 'processando';
  // "Demorando": import preso em 'processando' alem do esperado. O reaper do
  // backend marca 'erro' em ~20min; aqui so avisamos o usuario.
  const demorando = status === 'processando' && minutosDesde(item.dt_criacao) >= IMPORT_WARN_MIN;

  const handleAtribuir = async () => {
    if (!empresa) return;
    try {
      await atribuir({ importId: item.import_id, empresaId: empresa.value });
      queryClient.invalidateQueries({ queryKey: IMPORTS_KEY });
      toast({
        status: 'success',
        title: 'Empresa atribuída',
        description: 'Lançamentos importados.',
      });
    } catch (err) {
      toast({
        status: 'error',
        title: 'Falha ao atribuir empresa',
        description: extractApiErrorMessage(err, 'Tente novamente.'),
      });
    }
  };

  const renderControlsAtribuir = () => (
    <Flex
      gap={2}
      mt={3}
      direction={{ base: 'column', sm: 'row' }}
      align={{ base: 'stretch', sm: 'center' }}
    >
      <Box flex={1} minW={0}>
        <EmpresaPicker label="" value={empresa} onChange={setEmpresa} />
      </Box>
      <Button
        size="sm"
        colorScheme="orange"
        borderRadius="lg"
        onClick={handleAtribuir}
        isDisabled={!empresa}
        isLoading={atribuindo}
        w={{ base: 'full', sm: 'auto' }}
        flexShrink={0}
      >
        Atribuir
      </Button>
    </Flex>
  );

  const renderDetalhe = () => {
    if (status === 'erro') {
      return (
        <Text fontSize="11px" color="#EF4444" noOfLines={2}>
          {data?.erro ?? 'Falha no processamento'}
        </Text>
      );
    }
    if (status === 'a_identificar') {
      return (
        <Text fontSize="11px" color="#92400E" noOfLines={2}>
          Empresa não identificada no extrato
          {titular ? ` · ${titular}` : ''}
          {cnpj ? ` · ${cnpj}` : ''}
        </Text>
      );
    }
    if (status === 'aguardando_titular') {
      return (
        <Text fontSize="11px" color="#92400E" noOfLines={2}>
          Banco identificado · aguardando titular
          {titular ? ` · ${titular}` : ''}
        </Text>
      );
    }
    if (status === 'concluido') {
      const partes = [`${qtdNovas ?? 0} lançamentos`];
      if (banco) partes.push(banco);
      if (item.empresa_nome) partes.push(item.empresa_nome);
      const temBalanco = item.saldo_final != null || item.balanco_ok != null;
      return (
        <Box>
          <Text fontSize="11px" color="#64748B" isTruncated>
            {partes.join(' · ')}
          </Text>
          {temBalanco && (
            <Text
              fontSize="10px"
              color={item.balanco_ok === false ? '#F59E0B' : '#94A3B8'}
              isTruncated
            >
              {item.saldo_final != null && `saldo ${fmtBRL(item.saldo_final)}`}
              {item.balanco_ok != null &&
                ` · balanço ${
                  item.balanco_ok ? 'OK' : `diverge ${fmtBRL(item.divergencia_balanco ?? 0)}`
                }`}
            </Text>
          )}
        </Box>
      );
    }
    return (
      <Box>
        <Text fontSize="11px" color="#64748B">
          {data?.fase_label ?? 'Na fila'}
          {data?.detalhe ? ` — ${data.detalhe}` : ''}
        </Text>
        <Progress
          value={percent}
          size="xs"
          colorScheme={demorando ? 'orange' : 'blue'}
          borderRadius="full"
          mt={1}
          hasStripe
          isAnimated
        />
        {demorando && (
          <Text fontSize="10px" color="#F59E0B" mt={1}>
            Demorando mais que o esperado — se não concluir, reenvie o arquivo.
          </Text>
        )}
      </Box>
    );
  };

  return (
    <>
      <GlassCard
        intensity="medium"
        p={3}
        // Clicar no card leva pro extrato JÁ ABERTO em Extratos. Antes o card
        // era um beco: mostrava o resultado da importação e não levava a lugar
        // nenhum — quem quisesse ver o extrato tinha que ir na outra tela,
        // filtrar a empresa e procurar o arquivo na lista.
        //
        // Enquanto está processando não navega: ainda não há extrato montado
        // pra mostrar, e cair numa tela vazia é pior que não ter o atalho.
        onClick={podeAbrirExtrato ? abrirNoExtratos : undefined}
        cursor={podeAbrirExtrato ? 'pointer' : 'default'}
        role={podeAbrirExtrato ? 'button' : undefined}
        tabIndex={podeAbrirExtrato ? 0 : undefined}
        aria-label={podeAbrirExtrato ? `Abrir ${item.nome_arquivo} em Extratos` : undefined}
        onKeyDown={
          podeAbrirExtrato
            ? (ev: React.KeyboardEvent) => {
                // Só reage à tecla quando o foco está no PRÓPRIO card. Sem esta
                // linha o atalho engolia ESPAÇO e ENTER de tudo que vive dentro
                // dele: digitar a razão social no campo de empresa de um item "a
                // identificar" navegava para outra tela no primeiro espaço, com
                // o `preventDefault` impedindo até que o espaço fosse digitado.
                if (ev.target !== ev.currentTarget) return;
                if (ev.key === 'Enter' || ev.key === ' ') {
                  ev.preventDefault();
                  abrirNoExtratos();
                }
              }
            : undefined
        }
        _hover={podeAbrirExtrato ? { borderColor: 'rgba(99, 102, 241, 0.45)' } : undefined}
      >
        <Flex align="center" justify="space-between" gap={3}>
          <Flex align="center" gap={3} minW={0} flex={1}>
            <Box color={ui.cor} flexShrink={0}>
              {renderStatusIcon(status)}
            </Box>
            <Box minW={0} flex={1}>
              <Text fontSize="sm" fontWeight="500" color="#0F172A" isTruncated>
                {item.nome_arquivo}
              </Text>
              {renderDetalhe()}
            </Box>
          </Flex>
          <Flex align="center" gap={2} flexShrink={0}>
            {podeVisualizar && (
              <IconButton
                aria-label="Visualizar documento"
                icon={<Eye size={16} weight="duotone" />}
                size="xs"
                variant="ghost"
                borderRadius="full"
                // Sem o stopPropagation o clique no olho subiria pro card e
                // navegaria pra outra tela em vez de abrir o PDF.
                onClick={ev => {
                  ev.stopPropagation();
                  setPdfOpen(true);
                }}
              />
            )}
            <Badge colorScheme={ui.badge} borderRadius="full" px={2}>
              {badgeText(status, percent)}
            </Badge>
          </Flex>
        </Flex>
        {(status === 'a_identificar' || status === 'aguardando_titular') && (
          // Os controles de atribuir empresa vivem DENTRO do card clicável:
          // sem isolar o clique, mexer no select ou no botão navegaria pra
          // Extratos no meio da atribuição.
          <Box onClick={ev => ev.stopPropagation()}>{renderControlsAtribuir()}</Box>
        )}
      </GlassCard>
      <ExtratoPdfModal
        extratoId={pdfOpen ? item.import_id : null}
        nomeArquivo={item.nome_arquivo}
        formato={item.formato}
        isOpen={pdfOpen}
        onClose={() => setPdfOpen(false)}
      />
    </>
  );
}

/**
 * Miolo da importacao (dropzone + lista PERSISTENTE de imports). A empresa
 * NAO e escolhida no upload: o ETL detecta pelo CNPJ do extrato. Se nao
 * identificar, a linha fica 'a identificar' com atribuicao manual inline.
 * A lista carrega do banco (sobrevive a refresh). Reusado pela pagina
 * standalone e pelo modal.
 */
interface ImportarExtratoContentProps {
  initialFiltro?: ImportFiltro;
}

export function ImportarExtratoContent({
  initialFiltro = 'recentes',
}: ImportarExtratoContentProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useContaCilImportar();
  const { data: imports = [], isLoading } = useContaCilImports(undefined, FETCH_LIMIT);
  const [filtro, setFiltro] = useState<ImportFiltro>(initialFiltro);
  const [page, setPage] = useState(0);

  // Reabrir o modal por outro botao (ex: "Revisar") aplica o filtro inicial.
  useEffect(() => {
    setFiltro(initialFiltro);
    setPage(0);
  }, [initialFiltro]);

  const handleFiltro = (f: ImportFiltro) => {
    setFiltro(f);
    setPage(0);
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      // eslint-disable-next-line no-restricted-syntax
      for (const file of acceptedFiles) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const r = await mutateAsync({ file });
          toast({
            status: r.duplicado ? 'info' : 'success',
            title: r.duplicado ? 'Arquivo já importado' : 'Upload recebido',
            description: r.mensagem,
          });
        } catch (err) {
          toast({
            status: 'error',
            title: 'Falha no upload',
            description: extractApiErrorMessage(err, 'Tente novamente.'),
          });
        }
      }
      queryClient.invalidateQueries({ queryKey: IMPORTS_KEY });
    },
    [mutateAsync, toast, queryClient],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPT,
    disabled: isPending || !IMPORTACAO_MANUAL_HABILITADA,
  });

  const filtradas = useMemo(() => {
    if (filtro === 'pendentes') {
      // Tudo que ainda exige acao (nao concluido), 'a identificar' no topo.
      return imports
        .filter(i => i.status !== 'concluido')
        .sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
    }
    return imports; // recentes: backend ja ordena por data desc
  }, [imports, filtro]);

  const totalPages = Math.max(1, Math.ceil(filtradas.length / PAGE_SIZE));
  const pageAtual = Math.min(page, totalPages - 1);
  const pageItems = filtradas.slice(pageAtual * PAGE_SIZE, pageAtual * PAGE_SIZE + PAGE_SIZE);

  return (
    <Stack spacing={5}>
      {/* Dropzone (so com a importacao manual habilitada) ou aviso do fluxo por e-mail */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {IMPORTACAO_MANUAL_HABILITADA ? (
          <Box
            {...getRootProps()}
            cursor={isPending ? 'not-allowed' : 'pointer'}
            borderWidth="2px"
            borderStyle="dashed"
            borderColor={isDragActive ? '#1B88F5' : 'rgba(27, 136, 245, 0.3)'}
            borderRadius="3xl"
            bg={isDragActive ? 'rgba(27, 136, 245, 0.08)' : 'rgba(255, 255, 255, 0.55)'}
            backdropFilter="blur(20px) saturate(180%)"
            sx={{ WebkitBackdropFilter: 'blur(20px) saturate(180%)' }}
            transition="all 0.25s ease"
            p={{ base: 8, md: 10 }}
            textAlign="center"
          >
            <input {...getInputProps()} />
            <Stack align="center" spacing={3}>
              <motion.div
                animate={isDragActive ? { scale: 1.1, y: -4 } : { scale: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  w="72px"
                  h="72px"
                  borderRadius="2xl"
                  bg="linear-gradient(135deg, #E0E7FF 0%, #DDD6FE 100%)"
                  color="#6366F1"
                  boxShadow="0 12px 30px -8px rgba(99, 102, 241, 0.32)"
                >
                  {isPending ? (
                    <Spinner size="lg" color="#6366F1" thickness="3px" />
                  ) : (
                    <CloudArrowUp size={36} weight="duotone" />
                  )}
                </Box>
              </motion.div>
              <Box>
                <Text fontSize="md" fontWeight="600" color="#0F172A">
                  {dropzoneLabel(isPending, isDragActive)}
                </Text>
                <Text fontSize="xs" color="#64748B" mt={1}>
                  PDF, OFX, XLSX, CSV ou ZIP · 13+ bancos brasileiros
                </Text>
                <Text fontSize="xs" color="#94A3B8" mt={1}>
                  A empresa é identificada automaticamente pelo extrato.
                </Text>
              </Box>
            </Stack>
          </Box>
        ) : (
          <Box
            borderWidth="1px"
            borderColor="rgba(27, 136, 245, 0.3)"
            borderRadius="3xl"
            bg="rgba(255, 255, 255, 0.55)"
            backdropFilter="blur(20px) saturate(180%)"
            sx={{ WebkitBackdropFilter: 'blur(20px) saturate(180%)' }}
            p={{ base: 6, md: 8 }}
            textAlign="center"
          >
            <Stack align="center" spacing={3}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="72px"
                h="72px"
                borderRadius="2xl"
                bg="linear-gradient(135deg, #E0E7FF 0%, #DDD6FE 100%)"
                color="#6366F1"
                boxShadow="0 12px 30px -8px rgba(99, 102, 241, 0.32)"
              >
                <EnvelopeSimple size={36} weight="duotone" />
              </Box>
              <Box>
                <Text fontSize="md" fontWeight="600" color="#0F172A">
                  Importação automática de extratos
                </Text>
                <Text fontSize="xs" color="#64748B" mt={1}>
                  A importação manual está temporariamente desabilitada.
                </Text>
                <Text fontSize="xs" color="#94A3B8" mt={1}>
                  Para novas importações, envie o extrato pelo e-mail definido para o processo
                  automático.
                </Text>
              </Box>
            </Stack>
          </Box>
        )}
      </motion.div>

      {/* Lista persistente de imports (paginada) */}
      {imports.length > 0 && (
        <Box>
          <Flex align="center" justify="space-between" gap={2} mb={3} wrap="wrap">
            <Text fontSize="sm" fontWeight="600" color="#0F172A">
              Importações
            </Text>
            <Select
              size="xs"
              w="auto"
              borderRadius="lg"
              value={filtro}
              onChange={e => handleFiltro(e.target.value as ImportFiltro)}
            >
              <option value="recentes">Recentes</option>
              <option value="pendentes">Pendentes</option>
            </Select>
          </Flex>

          {pageItems.length === 0 ? (
            <Text fontSize="xs" color="#94A3B8" textAlign="center" py={2}>
              Nenhuma importação pendente.
            </Text>
          ) : (
            <Stack spacing={2}>
              {pageItems.map(item => (
                <ImportStatusRow key={item.import_id} item={item} />
              ))}
            </Stack>
          )}

          {totalPages > 1 && (
            <Flex align="center" justify="center" gap={3} mt={3}>
              <IconButton
                aria-label="Página anterior"
                icon={<CaretLeft size={14} weight="bold" />}
                size="xs"
                variant="ghost"
                borderRadius="full"
                isDisabled={pageAtual === 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
              />
              <Text fontSize="xs" color="#64748B" fontWeight="600">
                {pageAtual + 1} / {totalPages}
              </Text>
              <IconButton
                aria-label="Próxima página"
                icon={<CaretRight size={14} weight="bold" />}
                size="xs"
                variant="ghost"
                borderRadius="full"
                isDisabled={pageAtual >= totalPages - 1}
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              />
            </Flex>
          )}
        </Box>
      )}

      {!isLoading && imports.length === 0 && (
        <Text fontSize="xs" color="#94A3B8" textAlign="center">
          {IMPORTACAO_MANUAL_HABILITADA
            ? 'Nenhuma importação ainda. Arraste um extrato acima para começar.'
            : 'Nenhuma importação ainda.'}
        </Text>
      )}
    </Stack>
  );
}
